import { v4 as uuid } from 'uuid';
import { getAnonId, getSessionId, getUserId } from './identity';
import { PULSE_TAXONOMY, PulseEventName } from './taxonomy';
import { sendPulseBatch } from './transport';
import {
  PULSE_CONSENT_POLICY_VERSION,
  PULSE_CONSENT_TIER,
  PULSE_SOURCE,
  PulseEventEnvelope,
  PulseProps,
} from './types';

const MAX_BATCH = 50;
const FLUSH_INTERVAL_MS = 5000;
const MAX_RETRIES = 3;
const MAX_QUEUE = 1000; // safety valve: bound memory if the endpoint is persistently unreachable

type QueuedEvent = { attempts: number; event: PulseEventEnvelope };

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let flushing = false;
let transport = sendPulseBatch;

const isScalar = (v: unknown): v is string | number | boolean | null =>
  v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

// Mirrors the server allow-list in shared-contracts sanitiseEvent; taxonomy.spec.ts fails on
// drift. Duplicated rather than imported because the package's raw source does not compile
// under the web tsconfig (see shared-contracts-pulse.d.ts).
export const ALLOWED_QUERY_PARAMS = ['source', 'lang'];
export const ALLOWED_QUERY_PREFIXES = ['utm_'];
export const ALLOWED_CLICK_IDS = ['gclid', 'fbclid', 'li_fat_id'];

const isAllowedQueryParam = (key: string): boolean => {
  const k = key.toLowerCase();
  return (
    ALLOWED_QUERY_PARAMS.includes(k) ||
    ALLOWED_CLICK_IDS.includes(k) ||
    ALLOWED_QUERY_PREFIXES.some((prefix) => k.startsWith(prefix))
  );
};

// Strip the hash and every query param outside the allow-list. URLs in the embedded flow
// carry email/disease params (the signup handoff), so the default is to drop; the campaign
// params are kept because a paid click is otherwise untraceable to the patient it produced.
// The server re-applies the same allow-list and stays the authority.
const stripUrl = (raw: string | undefined): string | undefined => {
  if (!raw) return undefined;
  try {
    const u = new URL(raw);
    const kept = new URLSearchParams();
    u.searchParams.forEach((value, key) => {
      if (isAllowedQueryParam(key)) kept.append(key, value);
    });
    const query = kept.toString();
    return `${u.origin}${u.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return undefined;
  }
};

// Client-side convenience strip; the server is the authority. Keep only the
// taxonomy's declared keys, scalar values only.
const cleanProps = (
  name: PulseEventName,
  props?: Record<string, unknown>,
): PulseProps | undefined => {
  if (!props) return undefined;
  const out: PulseProps = {};
  PULSE_TAXONOMY[name].forEach((key) => {
    const value = props[key];
    if (value !== undefined && isScalar(value)) out[key] = value;
  });
  return Object.keys(out).length ? out : undefined;
};

const sendBatch = async (beacon: boolean): Promise<void> => {
  const batch = queue.splice(0, MAX_BATCH);
  if (batch.length === 0) return;
  try {
    await transport(
      batch.map((q) => q.event),
      { beacon },
    );
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    const retryable = status === undefined || status >= 500;
    if (retryable) {
      // Re-queue at the front for at-least-once delivery. Ordering is not preserved
      // across a failure, but the backend dedupes on event_id and orders on client_ts.
      const survivors = batch
        .map((q) => ({ event: q.event, attempts: q.attempts + 1 }))
        .filter((q) => q.attempts < MAX_RETRIES);
      queue.unshift(...survivors);
    }
    // swallow — best effort
  }
};

export const flushPulse = async (opts: { beacon?: boolean } = {}): Promise<void> => {
  if (queue.length === 0) return;
  // The unload beacon is exempt from the single-flight guard: it's the last chance to
  // drain the queue, and splice() is atomic so it can't double-send the regular flush's
  // in-flight batch (that batch survives unload via keepalive in the transport). Without
  // this exemption, a beacon firing mid-flush would bail and lose everything still queued.
  if (opts.beacon) {
    await sendBatch(true);
    return;
  }
  // Single-flight guard: overlapping regular flushes (5s timer / batch-threshold) would
  // spawn redundant in-flight requests. Bail if one is already running.
  if (flushing) return;
  flushing = true;
  try {
    await sendBatch(false);
  } finally {
    flushing = false;
  }
};

const startTimer = (): void => {
  if (timer || typeof window === 'undefined') return;
  timer = setInterval(() => {
    flushPulse().catch(() => undefined);
  }, FLUSH_INTERVAL_MS);
};

// `props` is `object` (not Record) so the typed helper interfaces in taxonomy.ts are
// assignable; cleanProps re-narrows to a scalar Record below.
export const enqueuePulseEvent = (name: PulseEventName, props?: object): void => {
  try {
    const anon = getAnonId();
    const envelope: PulseEventEnvelope = {
      eventId: uuid(),
      eventName: name,
      clientTs: new Date().toISOString(),
      cwAnonId: anon.id,
      cwSessionId: getSessionId(),
      cwUserId: getUserId(),
      anonIdStorage: anon.storage,
      source: PULSE_SOURCE,
      referrer: stripUrl(typeof document !== 'undefined' ? document.referrer : undefined),
      pageUrl: stripUrl(typeof window !== 'undefined' ? window.location.href : undefined),
      language:
        typeof document !== 'undefined' ? document.documentElement.lang || undefined : undefined,
      consentTier: PULSE_CONSENT_TIER,
      consentPolicyVersion: PULSE_CONSENT_POLICY_VERSION,
      props: cleanProps(name, props as Record<string, unknown> | undefined),
    };
    if (queue.length >= MAX_QUEUE) queue.shift(); // drop oldest under sustained backpressure
    queue.push({ event: envelope, attempts: 0 });
    startTimer();
    if (queue.length >= MAX_BATCH) flushPulse().catch(() => undefined);
  } catch {
    // analytics must never throw into product code
  }
};

export const setPulseTransportForTests = (fn: typeof sendPulseBatch): void => {
  transport = fn;
};
export const resetPulseClientForTests = (): void => {
  queue = [];
  if (timer) clearInterval(timer);
  timer = null;
  transport = sendPulseBatch;
};
